import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { BoundaryCategoryKey, BoundaryLevelKey } from '@llura/constants';

import { api } from '../lib/api-client';
import { syncFireAndForget } from '../lib/sync';

export type BoundaryCategory = BoundaryCategoryKey;
export type BoundaryLevel = BoundaryLevelKey;

/** All categories the user can set in the covenant step. Order matters — */
/** it's the order they appear on screen. */
export const BOUNDARY_CATEGORIES: readonly BoundaryCategory[] = [
  'intensity',
  'language',
  'duration',
  'haptic',
  'audioExplicit',
] as const;

export interface CovenantState {
  boundaries: Record<BoundaryCategory, BoundaryLevel>;
  /** Empty string ⇒ user chose to skip; safe-exit button + volume gesture still work. */
  safeWord: string;
  /** Whether the user has walked through the covenant step at least once. */
  hasAccepted: boolean;

  setBoundary: (category: BoundaryCategory, level: BoundaryLevel) => void;
  setBoundaries: (boundaries: Partial<Record<BoundaryCategory, BoundaryLevel>>) => void;
  setSafeWord: (word: string) => void;
  acceptCovenant: () => void;
  /** Overwrite local state with the server's view of the covenant. */
  hydrate: (input: {
    boundaries: Record<BoundaryCategory, BoundaryLevel>;
    safeWord: string;
    hasAccepted: boolean;
  }) => void;
  reset: () => void;
}

const defaultBoundaries: Record<BoundaryCategory, BoundaryLevel> = {
  intensity: 'firm',
  language: 'firm',
  duration: 'firm',
  haptic: 'firm',
  audioExplicit: 'soft',
};

const initial: Pick<CovenantState, 'boundaries' | 'safeWord' | 'hasAccepted'> = {
  boundaries: defaultBoundaries,
  safeWord: '',
  hasAccepted: false,
};

export const useCovenantStore = create<CovenantState>()(
  persist(
    (set) => ({
      ...initial,
      setBoundary: (category, level) => {
        set((s) => ({ boundaries: { ...s.boundaries, [category]: level } }));
        syncFireAndForget(() => api.me.updateCovenant({ boundaries: { [category]: level } }));
      },
      setBoundaries: (partial) => {
        set((s) => ({ boundaries: { ...s.boundaries, ...partial } }));
        syncFireAndForget(() => api.me.updateCovenant({ boundaries: partial }));
      },
      setSafeWord: (safeWord) => {
        const trimmed = safeWord.trim();
        set({ safeWord: trimmed });
        syncFireAndForget(() => api.me.updateCovenant({ safeWord: trimmed }));
      },
      acceptCovenant: () => set({ hasAccepted: true }),
      hydrate: (input) =>
        set({
          boundaries: input.boundaries,
          safeWord: input.safeWord,
          hasAccepted: input.hasAccepted,
        }),
      reset: () => set({ ...initial }),
    }),
    {
      name: 'llura.covenant',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
